import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import _ from 'lodash';
import {Box, CircularProgress} from '@mui/material';

// comps
import DialogTemplate from '../../components/dialogs/dialogTemplate';
import GlobalInput from '../../components/global/input';
import GlobalButton from '../../components/global/button';
import GlobalDropdown from '../../components/global/dropdown';
import ToastMessage from '../../components/global/toastMessage';

// icons
import { ReactComponent as WarningIcon } from '../../assets/icons/warning-icon.svg';
import GlobalRadioGroup, { GlobalRadioGroupOptions } from '../../components/global/radio';
import GlobalCheckbox from '../../components/global/checkbox';
import ContentContainer from "../../components/layout/contentContainer";

export interface CompanyDataItem {
    id?: number;
    name: string;
}

interface CompanyDialogProps {
    data: CompanyDataItem;
    onSave?: (item: CompanyDataItem) => void;
    onClose: () => void;
    companies: any;
    onCreate: (item: CompanyDataItem) => void;
}

const CompanyDialog = (props: CompanyDialogProps) => {

    const { data, onSave, onClose, onCreate } = props;
    const isCreate = !data.id;

    const [errorMsg, setErrorMsg] = useState(null);
    const [requiredFields, setRequiredFields] = useState([]);
    const [isClicked, setIsClicked] = useState(false);

    const [dataItem, setDataItem] = useState(data);

    const onChange = (name: any, value: any) => {
        setDataItem({
            ...dataItem,
            [name]: value,
        });
    };
    
    useEffect(() => {
        const requiredFieldsArr = ['name'];
        setRequiredFields(requiredFieldsArr.filter(field => {
            if (typeof dataItem[field] === 'string' && dataItem[field].trim() === '') return true;

            if (typeof dataItem[field] === 'number' && isNaN(dataItem[field])) return true;

            return (dataItem[field] === null || dataItem[field] === undefined);
        }));
    }, [dataItem]);

    const handleSave = () => {
        let item = { ...dataItem };

        setIsClicked((state) => !state);

        if (requiredFields.length === 0) {
            if (isCreate) onCreate(item);
            else {
                // If fields were NOT edited (= if the object is unchanged)
                if (_.isEqual(item, data)) {
                    toast.warning(`There were no changes made`);
                    onClose();
                } else {
                    onSave(item);
                }
            }
        } else {
            setErrorMsg(`The required fields must be filled in order to proceed.`);
        }
    };

    const handleErrorState = (name: string) => {
        if (isClicked) {
            return requiredFields?.includes(name);
        }
    };

    const renderContent = () => {
        return (
            <Container>
                {errorMsg && (
                    <FlexBox>
                        <ToastMessage isError msg={errorMsg} icon={<WarningIcon />} />
                    </FlexBox>
                )}
                <FlexBox>
                    <InputBox sx={{ '& > *': { mr: '10px' } }}>
                        <Label>Company Name <span>*</span></Label>
                        <GlobalInput
                            isRequired
                            isError={handleErrorState('name')}
                            placeholder="Company Name"
                            value={dataItem.name}
                            name={'name'}
                            onChange={onChange}
                        />
                    </InputBox>
                </FlexBox>
                <FlexBox sx={{ mt: '2em' }}>
                    <GlobalButton text="cancel" variant="outlined" onClick={onClose} />
                    <GlobalButton text={isCreate ? 'create' : 'save'} variant="contained" onClick={handleSave} />
                </FlexBox>
            </Container>
        );
    };

    return (
        <>
            <DialogTemplate {...props} title={'Add Company'} renderContent={renderContent} />
        </>
    );
};

export default CompanyDialog;

const Container = styled(Box)`
    && {
        width: 100%;
        padding: 1em 0px;
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }
`;

const InputBox = styled(Box)`
    && {
        width: 100%;
        & > .MuiBox-root {
            margin-bottom: 1em;
        }
    }
`;

const Label = styled.div`
    color: #0085E6;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 2px;

    & > span {
        color: #D73948;
    }
`
